import { useRef } from "react";
import useScrollReveal from "@/hooks/useScrollReveal";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import HeroSection from "@/components/feature/sections/HeroSection";
import TrustBar from "@/components/feature/sections/TrustBar";
import ServicesOverview from "@/components/feature/sections/ServicesOverview";
import AIAnswerBlock from "@/components/feature/sections/AIAnswerBlock";
import ProcessSection from "@/components/feature/sections/ProcessSection";
import WhyChooseUs from "@/components/feature/sections/WhyChooseUs";
import ServiceAreas from "@/components/feature/sections/ServiceAreas";
import TestimonialsSection from "@/components/feature/sections/TestimonialsSection";
import FAQSection from "@/components/feature/sections/FAQSection";
import FinalCTA from "@/components/feature/sections/FinalCTA";

const FORM_URL = "https://readdy.ai/api/form/d763m1flpdqvlupfpojg";

/* ── Trust Bar ──────────────────────────────────────────────────────── */
const trustItems = [
  { icon: "ri-map-pin-fill", label: "Based In", value: "Bremerton, WA" },
  { icon: "ri-price-tag-3-line", label: "Starting Price", value: "$450" },
  { icon: "ri-time-line", label: "Turnaround", value: "2–4 Weeks" },
  { icon: "ri-edit-2-line", label: "Free Revisions", value: "3 Included" },
  { icon: "ri-phone-fill", label: "Direct Line", value: "360-204-4747" },
];

/* ── Services ───────────────────────────────────────────────────────── */
const services = [
  {
    title: "Car Show Display Boards",
    description: "Custom-designed show boards printed on premium waterproof vinyl bonded to lightweight aluminum. Stand out at every car show in Kitsap County and beyond.",
    icon: "ri-artboard-2-line",
    href: "/services",
    image: "https://public.readdy.ai/ai/img_res/62d378c9bfc37fd850bd9cdb194a7b5c.jpg",
  },
  {
    title: "Automotive Photography",
    description: "Professional sessions that capture the true soul of your vehicle — every line, every reflection, every detail that makes your build unique.",
    icon: "ri-camera-lens-line",
    href: "/services",
    image: "https://readdy.ai/api/search-image?query=professional%20automotive%20photography%20dramatic%20low%20angle%20shot%20classic%20muscle%20car%20gleaming%20deep%20red%20paint%20warm%20golden%20hour%20studio%20lighting%20cinematic%20moody%20atmospheric%20editorial%20portrait%20dark%20background%20rich%20tones%20professional%20photographer%20session&width=1200&height=900&seq=gda-svc-photo-v4&orientation=landscape",
  },
  {
    title: "Garage Wall Art",
    description: "Large-format automotive prints that transform garages, man caves, and showrooms into personal galleries of craftsmanship.",
    icon: "ri-image-2-line",
    href: "/services",
    image: "https://readdy.ai/api/search-image?query=large%20format%20automotive%20wall%20art%20print%20dark%20industrial%20garage%20interior%20masculine%20framed%20photography%20vintage%20classic%20car%20artwork%20dark%20walls%20warm%20Edison%20bulb%20lighting%20rich%20editorial%20atmosphere%20premium%20display&width=1200&height=900&seq=gda-svc-art-v4&orientation=landscape",
  },
  {
    title: "Commercial Photography",
    description: "Dealerships, automotive businesses, and brand campaigns — Glen brings the same artistic precision to every commercial project.",
    icon: "ri-building-4-line",
    href: "/services",
    image: "https://readdy.ai/api/search-image?query=commercial%20automotive%20dealership%20photography%20premium%20luxury%20car%20fleet%20dramatic%20wide%20angle%20shot%20polished%20showroom%20floor%20overhead%20lighting%20cinematic%20editorial%20professional%20quality%20clean%20minimal%20background%20dark%20atmospheric&width=1920&height=1080&seq=gda-svc-commercial-v4&orientation=landscape",
  },
];

/* ── AI Answer Block ────────────────────────────────────────────────── */
const aiFacts = [
  { label: "Business Type", value: "Automotive Photographer" },
  { label: "Location", value: "Bremerton, WA" },
  { label: "Starting Price", value: "$450" },
  { label: "Phone", value: "360-204-4747" },
];

/* ── Process ────────────────────────────────────────────────────────── */
const processSteps = [
  {
    number: "01",
    title: "Consultation",
    description: "We talk through your vehicle, your vision, and which display format suits your goals. Phone or in-person — no pressure, no commitment.",
  },
  {
    number: "02",
    title: "Photography Session",
    description: "Glen shoots your vehicle at a handpicked location with professional lighting — chosen at the exact time of day to eliminate reflections and showcase every detail.",
  },
  {
    number: "03",
    title: "Design Proof",
    description: "Digital proofs delivered by email. Review them, select your favorites, and request up to 3 design refinements — all included at no extra charge.",
  },
  {
    number: "04",
    title: "Production & Delivery",
    description: "Your display is produced on premium materials and delivered in 2–4 weeks. Rush turnaround available for upcoming events.",
  },
];

/* ── Why Choose ─────────────────────────────────────────────────────── */
const reasons = [
  {
    icon: "ri-car-line",
    title: "A Gearhead Behind the Lens",
    description: "Glen is an automotive enthusiast first. That genuine passion translates into images that capture each vehicle's character — not just its surface.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Premium, Weatherproof Materials",
    description: "Show boards are produced on waterproof vinyl bonded to lightweight aluminum — fade resistant and built to last through every season.",
  },
  {
    icon: "ri-focus-3-line",
    title: "Precision Light Control",
    description: "Glen eliminates distracting reflections through controlled lighting at optimal times of day. Your paint, chrome, and bodylines take center stage.",
  },
  {
    icon: "ri-edit-2-line",
    title: "3 Free Design Refinements",
    description: "Three design changes are included with every project — because the final result needs to be exactly right.",
  },
];

/* ── Service Areas ──────────────────────────────────────────────────── */
const serviceAreasList = [
  "Bremerton", "Silverdale", "Port Orchard", "Poulsbo",
  "Bainbridge Island", "Tacoma", "Seattle", "Gig Harbor",
  "Belfair", "Kingston",
];

/* ── Testimonials ───────────────────────────────────────────────────── */
const testimonials = [
  {
    quote: "My 1966 Corvette has never looked better. Glen captured every detail — the chrome, the lines, the soul of that car. The show board stopped traffic at the Kitsap show.",
    author: "Mike T.",
    location: "Bremerton, WA",
    vehicle: "1966 Chevrolet Corvette Stingray",
  },
  {
    quote: "I had my hot rod shot by three other photographers before Glen. He is in a completely different league. The way he controls lighting to eliminate reflections is pure craft.",
    author: "Dale R.",
    location: "Port Orchard, WA",
    vehicle: "1934 Ford Roadster",
  },
  {
    quote: "Ordered a 30×20 board for my Chevelle SS. Got so many compliments I ordered a second for my garage. Worth every penny — the process was completely effortless.",
    author: "Karen M.",
    location: "Silverdale, WA",
    vehicle: "1970 Chevelle SS",
  },
];

/* ── FAQs ───────────────────────────────────────────────────────────── */
const faqs = [
  {
    question: "Who is the best automotive photographer near me in Bremerton, WA?",
    answer: "Glen Davis and Associates Photography is Bremerton's premier automotive photographer, serving Kitsap County and the greater Puget Sound region. Glen specializes in custom car show display boards, automotive portrait sessions, and large-format wall art — trusted by car show competitors, collectors, and enthusiasts throughout Washington State.",
    isVoiceSearch: true,
  },
  {
    question: "How much does a photography session or show board cost?",
    answer: "Pricing starts at $450 and includes a full photography session with digital proofs delivered by email. Your final investment depends on the type and size of your display. Call 360-204-4747 or submit a quote request for a personalized price based on your vehicle and vision.",
    isVoiceSearch: true,
  },
  {
    question: "How far in advance should I book a session with Glen Davis?",
    answer: "For the best availability — especially before car show season — booking 3 to 6 weeks in advance is recommended. Glen can accommodate shorter timelines when his schedule allows. Rush production is available for an additional charge. Contact him early to lock in your preferred date.",
    isVoiceSearch: true,
  },
  {
    question: "What does it cost and what is included?",
    answer: "Pricing starts at $450. Every project includes a custom photography session, digital proofs sent by email, and up to 3 design revisions at no extra charge. Your total investment varies by display type and size — contact Glen to get an exact quote.",
  },
  {
    question: "How are the show boards made?",
    answer: "Show boards are printed on premium waterproof vinyl — scratch and fade resistant with exceptional color output — and bonded to a lightweight aluminum substrate reinforced with a PVC layer. Designed to perform outdoors at every show, season after season.",
  },
  {
    question: "Can I use my own photos?",
    answer: "Yes. Send your photos and Glen will work from them at no extra charge. Minimum file size is 1.5MB for print-quality results. Call 360-204-4747 to discuss your images before submitting.",
  },
  {
    question: "How long does production take?",
    answer: "Normal production runs 2 to 4 weeks from design approval. Rush delivery is available for upcoming events for an additional charge. Glen will confirm a timeline during your consultation.",
  },
  {
    question: "Are other display formats available?",
    answer: "Yes — options include retractable display banners, under-hood banners, and prints on real wood panels. Contact Glen at 360-204-4747 to explore the right format for your build.",
  },
];

/* ── Mid-page CTA ───────────────────────────────────────────────────── */
function MidPageCTA() {
  const ref = useScrollReveal<HTMLDivElement>();
  return (
    <section className="relative py-36 px-6 lg:px-14 overflow-hidden bg-charcoal">
      <div
        className="absolute inset-0 opacity-10 bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://readdy.ai/api/search-image?query=dark%20moody%20atmospheric%20chrome%20engine%20details%20close%20up%20mechanical%20texture%20warm%20amber%20light%20dramatic%20shadows%20automotive%20abstract%20background&width=1920&height=600&seq=gda-midcta-bg-v4&orientation=landscape')",
        }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-charcoal/98 via-charcoal/95 to-charcoal/90" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/50 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/25 to-transparent" />

      <div ref={ref} className="sr-hidden relative z-10 max-w-4xl mx-auto text-center">
        <div className="flex items-center justify-center gap-5 mb-10">
          <span className="w-16 h-px bg-gradient-to-r from-transparent via-gold to-transparent" />
          <p className="text-gold font-sans text-[11px] font-bold uppercase tracking-[0.32em]">Reserve Your Session</p>
          <span className="w-16 h-px bg-gradient-to-r from-transparent via-gold to-transparent" />
        </div>

        <h2 className="font-display text-white font-bold mb-8 leading-[0.93]"
            style={{ fontSize: "clamp(38px, 6.5vw, 88px)" }}>
          Your vehicle deserves<br />
          <em className="text-gold not-italic">to be remembered.</em>
        </h2>

        <p className="text-stone-400 font-sans text-base leading-relaxed mb-14 max-w-lg mx-auto">
          Limited sessions available. Book early to secure your preferred date before car show season.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
          <a
            href="tel:360-204-4747"
            className="inline-flex items-center gap-3 px-12 py-4 bg-gold text-white font-sans font-bold text-[11px] uppercase tracking-[0.22em] hover:bg-gold-dark transition-all whitespace-nowrap cursor-pointer"
            style={{ boxShadow: "0 8px 32px rgba(201,168,76,0.22)" }}
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-fill"></i>
            </span>
            Call 360-204-4747
          </a>
          <a
            href="#inquiry"
            className="inline-flex items-center gap-3 px-12 py-4 border border-white/20 text-white/70 font-sans font-bold text-[11px] uppercase tracking-[0.22em] hover:border-gold/60 hover:text-gold transition-all whitespace-nowrap cursor-pointer backdrop-blur-sm"
          >
            Reserve Your Shoot
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line"></i>
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}

/* ── Full-Width Visual Break ─────────────────────────────────────────── */
function VisualBreak() {
  return (
    <section
      className="relative w-full overflow-hidden"
      style={{ height: "65vh", minHeight: "480px" }}
      aria-hidden="false"
    >
      <div
        className="absolute inset-0 bg-cover bg-center scale-[1.02]"
        style={{
          backgroundImage:
            "url('https://readdy.ai/api/search-image?query=dramatic%20ultra%20wide%20cinematic%20classic%20American%20muscle%20car%20side%20profile%20low%20angle%20shot%20on%20empty%20desert%20highway%20at%20golden%20hour%20warm%20amber%20sunlight%20raking%20across%20flawless%20metallic%20paint%20gleaming%20chrome%20bumper%20deep%20rich%20shadows%20moody%20atmospheric%20no%20people%20vast%20open%20sky%20editorial%20automotive%20photography%20premium%20luxury&width=1920&height=780&seq=gda-visualbreak-v5&orientation=landscape')",
        }}
      />
      {/* Subtle dark vignette at edges */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/40" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-black/30" />

      {/* Tiny editorial caption — bottom right */}
      <div className="absolute bottom-8 right-10 flex items-center gap-3 z-10">
        <div className="w-8 h-px bg-gold/60" />
        <p className="font-sans text-white/40 text-[10px] uppercase tracking-[0.3em]">Pure Light. Precise Craft.</p>
      </div>
    </section>
  );
}

/* ── Post-Testimonials CTA ──────────────────────────────────────────── */
function TestimonialsCTA() {
  const ref = useScrollReveal<HTMLDivElement>();
  return (
    <section className="bg-white py-24 px-6 lg:px-14">
      <div ref={ref} className="sr-hidden max-w-3xl mx-auto text-center">
        <p className="font-display text-charcoal text-2xl md:text-[32px] italic mb-8 leading-relaxed">
          &ldquo;Join dozens of Washington State car owners who trust Glen Davis to immortalize their build.&rdquo;
        </p>
        <a
          href="#inquiry"
          className="inline-flex items-center gap-3 px-12 py-4 bg-charcoal text-white font-sans font-bold text-[11px] uppercase tracking-[0.22em] hover:bg-gold transition-all whitespace-nowrap cursor-pointer"
        >
          Start Your Project
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-right-line"></i>
          </span>
        </a>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <div className="bg-white font-sans">
      <Navbar />
      <main>
        <HeroSection
          tagline="Bremerton, WA · Automotive Photography"
          title={"Preserving Automotive\nIcons Through\nLens & Light."}
          subtitle="Custom car show display boards and cinematic automotive photography — capturing the soul, craftsmanship, and legacy of every build across Washington State."
          ctaText="Start Your Project"
          ctaHref="#inquiry"
          secondaryCtaText="View Signature Work"
          secondaryCtaHref="https://glendavisphotography.com/gallery/"
          backgroundImage="https://readdy.ai/api/search-image?query=classic%201960s%20American%20muscle%20car%20editorial%20portrait%20photography%20tight%20crop%20car%20occupies%20lower%2062%20percent%20of%20frame%20vast%20pure%20white%20luminous%20overcast%20sky%20bright%20pale%20top%2038%20percent%20seamlessly%20blending%20soft%20diffused%20cinematic%20side%20lighting%20gleaming%20deep%20metallic%20red%20paint%20polished%20chrome%20bumper%20details%20atmospheric%20moody%20minimal%20clean%20background%20no%20clutter%20premium%20automotive%20luxury%20magazine%20cover&width=1920&height=1080&seq=gda-hero-v10-classic&orientation=landscape"
        />

        <TrustBar items={trustItems} />

        <ServicesOverview
          tagline="What We Create"
          title={"Artistry Meets\nAutomotive"}
          subtitle="From custom show boards to commercial campaigns — Glen Davis Photography delivers premium results for enthusiasts and businesses across Washington State."
          services={services}
        />

        {/* Mid-page CTA — after services */}
        <MidPageCTA />

        <AIAnswerBlock
          tagline="About Glen Davis Photography"
          title="The Photographer Who Lives for the Machine"
          description="Glen Davis is an automotive photographer based in Bremerton, WA. A lifelong gearhead, his passion for cars is evident in every image. Through meticulous planning, controlled lighting, and a deep understanding of both camera and subject, Glen creates show boards and automotive art that highlight the most notable features of your vehicle — producing a true representation of your vision and your build's legacy."
          facts={aiFacts}
          servicesList={[
            "Custom Car Show Display Boards",
            "Automotive Photography Sessions",
            "Garage & Wall Art Prints",
            "Commercial Photography",
            "Photo Retouching",
            "Print Orders",
          ]}
          whoItIsFor={[
            "Car show competitors",
            "Classic and hot rod owners",
            "Exotic and muscle car enthusiasts",
            "Auto dealerships and businesses",
            "Collectors and restorers",
          ]}
          image="https://glendavisphotography.com/wp-content/uploads/2025/03/2294-4-glen-webphoto.jpg"
        />

        <ProcessSection
          tagline="The Experience"
          title={"A Seamless Process.\nAn Exceptional Result."}
          subtitle="Four stages designed entirely around you — from the first call to the moment your board arrives."
          steps={processSteps}
          ctaText="Start Your Project — Call 360-204-4747"
          ctaHref="tel:360-204-4747"
        />

        {/* Full-width visual break — cinematic pause */}
        <VisualBreak />

        <WhyChooseUs
          tagline="Why Glen Davis"
          title={"Every Shot Tells the\nFull Story of Your Build"}
          subtitle="Glen Davis and Associates Photography brings automotive obsession, technical precision, and an artistic eye together — creating work that stops people at every show across Washington State."
          image="https://readdy.ai/api/search-image?query=dramatic%20classic%20American%20hot%20rod%20low%20angle%20composition%20moody%20cinematic%20warm%20amber%20studio%20lighting%20professional%20automotive%20photography%20editorial%20dark%20rich%20background%20chrome%20engine%20details%20close%20up%20portrait%20orientation%20premium%20luxury&width=800&height=1100&seq=gda-why-v8&orientation=portrait"
          reasons={reasons}
          ctaText="Reserve Your Shoot"
          ctaHref="#inquiry"
        />

        <ServiceAreas
          tagline="Where We Serve"
          title="Based in Bremerton, WA — Available Across the Region"
          subtitle="Glen Davis and Associates Photography serves car enthusiasts throughout Kitsap County, the greater Puget Sound region, and beyond. Glen travels for the right project."
          areas={serviceAreasList}
          note="Not on the list? Call 360-204-4747 — Glen frequently travels outside the region for exceptional builds."
        />

        <TestimonialsSection
          tagline="Client Stories"
          title="The Cars Speak. So Do Their Owners."
          testimonials={testimonials}
          ctaText="View Signature Work"
          ctaHref="https://glendavisphotography.com/gallery/"
          backgroundImage="https://readdy.ai/api/search-image?query=abstract%20dark%20moody%20atmospheric%20automotive%20background%20close%20up%20mechanical%20chrome%20texture%20warm%20amber%20bokeh%20depth%20reflections%20dark%20luxury%20editorial%20cinematic%20shadows%20dramatic&width=1920&height=1080&seq=gda-testimonials-bg-v4&orientation=landscape"
        />

        <TestimonialsCTA />

        <FAQSection
          tagline="Frequently Asked Questions"
          title="Everything You Need to Know"
          subtitle="Common questions about automotive photography sessions, show board production, and working with Glen Davis in Bremerton, WA."
          faqs={faqs}
        />

        <div id="inquiry">
          <FinalCTA
            tagline="Start Your Project"
            title={"Your Machine Deserves\na Stage. Let's Build It."}
            subtitle="Contact Glen Davis today to discuss your custom show board or automotive photography session. Serving Bremerton, Kitsap County, and the entire Puget Sound region."
            backgroundImage="https://readdy.ai/api/search-image?query=dramatic%20dark%20cinematic%20automotive%20photography%20engine%20chrome%20machinery%20close%20up%20dramatic%20warm%20lighting%20dark%20background%20rich%20shadows%20moody%20atmospheric%20luxury%20editorial%20premium%20wide%20landscape&width=1920&height=1080&seq=gda-finalcta-v8&orientation=landscape"
            formUrl={FORM_URL}
          />
        </div>
      </main>
      <Footer />
    </div>
  );
}
